import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { onSummaryOpen, emitMeetingChanged, logUiEvent } from '../../lib/events';
import { downloadQuotationPdf, shareQuotationWhatsApp } from '../../lib/quotations';
import { toast } from 'sonner';
import logger from '../../lib/logger';

const SUMMARY_URLS = {
  lead: (id) => `/leads/${id}/summary`,
  quotation: (id) => `/quotations/${id}/summary`,
  staff: (id) => `/staff/${id}/summary`,
};

/** Meeting-specific panel state: the globally rendered MeetingSidePanel +
 * outcome/edit dialogs, usable over any page. */
const useMeetingPanel = () => {
  const [meeting, setMeeting] = useState(null);
  const [meetingMeta, setMeetingMeta] = useState(null);
  const [outcomeFor, setOutcomeFor] = useState(null);
  const [editMeeting, setEditMeeting] = useState(null);

  const ensureMeta = useCallback(async () => {
    if (meetingMeta) return meetingMeta;
    try {
      const { data: mm } = await api.get('/meetings/meta');
      setMeetingMeta(mm);
      return mm;
    } catch (e) {
      logger.warn('Meeting meta unavailable', e);
      return null;
    }
  }, [meetingMeta]);

  const onChanged = useCallback((updated) => {
    if (updated) setMeeting(updated);
    emitMeetingChanged(updated || null);
  }, []);

  const reopen = useCallback(async (id) => {
    try {
      const { data: m } = await api.get(`/meetings/${id}`);
      setMeeting(m);
    } catch { setMeeting(null); }
  }, []);

  return { meeting, setMeeting, meetingMeta, ensureMeta, outcomeFor, setOutcomeFor, editMeeting, setEditMeeting, onChanged, reopen };
};

/** State + actions for the panel: which summary is open, lazy option loading,
 * the Search-to-Action lead editor and the global meeting side panel. */
export const useSummaryPanel = () => {
  const [target, setTarget] = useState(null); // {type, id}
  const [data, setData] = useState(null);
  const [editLead, setEditLead] = useState(null);
  const [members, setMembers] = useState([]);
  const [staffOptions, setStaffOptions] = useState({ managers: [] });
  const meetingPanel = useMeetingPanel();
  const navigate = useNavigate();

  const ensureMembers = useCallback(async () => {
    if (members.length) return members;
    try {
      const { data: m } = await api.get('/team-members');
      setMembers(m);
      return m;
    } catch (e) {
      logger.error('Failed to load team members for the lead editor', e);
      toast.error('Could not load team members \u2014 assignment dropdowns may be empty');
      return [];
    }
  }, [members]);

  const ensureStaffOptions = useCallback(async () => {
    try {
      const { data: rows } = await api.get('/staff');
      setStaffOptions({ managers: rows });
    } catch (e) { logger.warn('Staff options unavailable', e); }
  }, []);

  const markFailure = useCallback((type, id, e) => {
    const notFound = e?.response?.status === 404;
    setData({ error: notFound ? 'not_found' : 'load' });
    logUiEvent(notFound ? 'RECORD_NOT_FOUND' : 'RECORD_OPEN_ERROR',
      { type, id, details: e?.response?.data?.detail || e?.message || '' });
  }, []);

  // Search-to-Action: a lead result opens the Edit Lead drawer directly.
  const openLeadEditorById = useCallback(async (id) => {
    setTarget({ type: 'lead-loading', id }); // shows the loading sheet immediately
    try {
      const [{ data: lead }] = await Promise.all([api.get(`/leads/${id}`), ensureMembers()]);
      setTarget(null);
      setEditLead(lead);
      logUiEvent('RECORD_OPENED', { type: 'lead', id, name: lead.lead_name });
    } catch (e) {
      markFailure('lead', id, e);
    }
  }, [ensureMembers, markFailure]);

  // Meeting result opens the full meeting side panel over the current page.
  const { ensureMeta, setMeeting } = meetingPanel;
  const openMeetingById = useCallback(async (id) => {
    setTarget({ type: 'meeting', id }); // loading sheet while fetching
    try {
      const [{ data: m }] = await Promise.all([api.get(`/meetings/${id}`), ensureMeta()]);
      setTarget(null);
      setMeeting(m);
      logUiEvent('RECORD_OPENED', { type: 'meeting', id, name: m.title });
    } catch (e) {
      markFailure('meeting', id, e);
    }
  }, [ensureMeta, setMeeting, markFailure]);

  useEffect(() => onSummaryOpen((e) => {
    const { type, id } = e.detail || {};
    if (type === 'lead') { openLeadEditorById(id); return; }
    if (type === 'meeting') { openMeetingById(id); return; }
    setTarget(e.detail);
    setData(null);
    if (type === 'staff') ensureStaffOptions();
  }), [openLeadEditorById, openMeetingById, ensureStaffOptions]);

  const load = useCallback(async () => {
    if (!target || target.type === 'lead-loading' || target.type === 'meeting') return;
    try {
      const url = (SUMMARY_URLS[target.type] || SUMMARY_URLS.staff)(target.id);
      const { data: d } = await api.get(url);
      setData(d);
      const name = d.lead?.lead_name || d.member?.name || d.quotation?.number || '';
      logUiEvent('RECORD_OPENED', { type: target.type, id: target.id, name });
    } catch (e) {
      markFailure(target.type, target.id, e);
    }
  }, [target, markFailure]);

  useEffect(() => { load(); }, [load]);

  const openLeadEditor = useCallback(async () => {
    await ensureMembers();
    setEditLead(data.lead);
    setTarget(null);
  }, [ensureMembers, data]);

  const onStaffSaved = useCallback(() => { load(); }, [load]);

  const openStaffDirectory = useCallback(() => {
    const id = data?.member?.id;
    setTarget(null);
    navigate(id ? `/team?edit=${id}` : '/team');
  }, [data, navigate]);

  const quotationAction = useCallback(async (action) => {
    const q = data?.quotation;
    if (!q) return;
    if (action === 'download') {
      try { await downloadQuotationPdf(api, q.id, q.number); toast.success('PDF downloaded'); }
      catch { toast.error('Download failed'); }
      return;
    }
    if (action === 'whatsapp') {
      try {
        await shareQuotationWhatsApp(api, q.id);
        toast.success('WhatsApp opened \u2014 quotation link shared');
      } catch (e) { toast.error(e.response?.data?.detail || 'WhatsApp share failed'); }
      return;
    }
    setTarget(null);
    navigate(`/quotations/${q.id}/edit${action === 'send' ? '?send=1' : ''}`);
  }, [data, navigate]);

  return {
    target, setTarget, data, editLead, setEditLead, members, staffOptions,
    openLeadEditor, onStaffSaved, openStaffDirectory, quotationAction,
    ...meetingPanel,
  };
};
